import React from 'react';
import { Box, Typography, Card, CardContent, Button } from '@mui/material';
import { EventNote, Timeline, LibraryBooks, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const EmptyState = ({ type = 'timeline', onActionClick, showAction = false, actionText, caseid }) => {
  const { t } = useTranslation();

  const getEmptyStateConfig = () => {
    switch (type) {
      case 'timeline':
        return {
          icon: Timeline,
          title: t('timeline.no_events_title', 'No Timeline Events'),
          description: t('timeline.no_events_description', 'Your timeline is empty. Start by adding files to your case or creating events manually.'),
          illustration: '📊',
          actionText: actionText || t('timeline.add_first_event', 'Add First Event')
        };
      case 'eventlist':
        return {
          icon: EventNote,
          title: t('eventlist.no_events_title', 'No Events Found'),
          description: t('eventlist.no_events_description', 'No events match your current filters or there are no events in this case yet.'),
          illustration: '📋',
          actionText: actionText || t('eventlist.add_first_event', 'Add First Event')
        };
      case 'general':
      default:
        return {
          icon: LibraryBooks,
          title: t('empty.no_data_title', 'No Data Available'),
          description: t('empty.no_data_description', 'There is no data to display at this time.'),
          illustration: '📁',
          actionText: actionText || t('empty.get_started', 'Get Started')
        };
    }
  };

  const config = getEmptyStateConfig();
  const IconComponent = config.icon;

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '50vh',
      p: 3 
    }}>
      <Card sx={{ 
        borderRadius: 5,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(87, 87, 176, 0.1)'
      }}>
        {/* Animated background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 50%, rgba(87,87,176,0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(87,87,176,0.05) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(87,87,176,0.05) 0%, transparent 50%)
          `,
          animation: 'gentleFloat 8s ease-in-out infinite',
          '@keyframes gentleFloat': {
            '0%, 100%': { transform: 'translate(0px, 0px) rotate(0deg)' },
            '33%': { transform: 'translate(10px, -10px) rotate(60deg)' },
            '66%': { transform: 'translate(-10px, 10px) rotate(120deg)' }
          }
        }} />
        
        <CardContent sx={{ 
          p: 4,
          position: 'relative',
          zIndex: 1
        }}>
          {/* Icon Container */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3
          }}>
            {/* Illustration */}
            <Box sx={{
              fontSize: '4rem',
              mb: 2,
              opacity: 0.8,
              animation: 'bounce 2s infinite ease-in-out',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-10px)' }
              }
            }}>
              {config.illustration}
            </Box>
            
            {/* Icon Circle */}
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(145deg, #5757b0 0%, #6d6dc8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: 'inherit',
                opacity: 0.3,
                animation: 'pulse 2s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.1 }
                }
              }
            }}>
              <IconComponent sx={{ 
                fontSize: 40, 
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }} />
            </Box>
          </Box>

          {/* Content */}
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2,
              color: '#5757b0',
              fontWeight: 600,
              letterSpacing: '0.02em'
            }}
          >
            {config.title}
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              mb: 3,
              lineHeight: 1.6,
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            {config.description}
          </Typography>

          {/* Action Button */}
          {showAction && onActionClick && (
            <Button
              variant="contained"
              onClick={onActionClick}
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(145deg, #5757b0 0%, #6d6dc8 100%)',
                color: 'white',
                borderRadius: '25px',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 16px rgba(87, 87, 176, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(145deg, #4a4a90 0%, #5d5db0 100%)',
                  boxShadow: '0 6px 20px rgba(87, 87, 176, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {config.actionText}
            </Button>
          )}

          {/* Subtle decorative elements */}
          <Box sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.3)',
            opacity: 0.5,
            animation: 'float 4s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0px, 0px)' },
              '50%': { transform: 'translate(-5px, -5px)' }
            }
          }} />
          
          <Box sx={{
            position: 'absolute',
            bottom: 30,
            left: 30,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.4)',
            opacity: 0.6,
            animation: 'float 3s ease-in-out infinite 1s',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0px, 0px)' },
              '50%': { transform: 'translate(3px, -3px)' }
            }
          }} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmptyState;